import type {
    JobTypeInfoItem,
} from "@mrflark/core/hooks";
import {
    formatRelativeTime,
} from "@mrflark/core/utils";
import {
    Play,
} from "lucide-preact";
import type {
    ComponentChildren,
} from "preact";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    StatusDot,
} from "../mod.tsx";
import {
    getJobTypeLabel,
} from "./types.ts";

type JobTypeCardsProps = {
    jobTypes: JobTypeInfoItem[];
    jobTypeLabels?: Record<string, string>;
    onRunNow?: (jobType: string) => void;
    onRequestRunWithPayload?: (typeInfo: JobTypeInfoItem) => void;
    vertical?: boolean;
};

const isInvocable = (typeInfo: JobTypeInfoItem): boolean =>
    typeInfo.manuallyInvocable !== false;

const hasPayloadFields = (typeInfo: JobTypeInfoItem): boolean =>
    (typeInfo.payloadFields?.length ?? 0) > 0;

export const JobTypeCards = ({
    jobTypes,
    jobTypeLabels,
    onRunNow,
    onRequestRunWithPayload,
    vertical = false,
}: JobTypeCardsProps): ComponentChildren => {
    const handleRunClick = (typeInfo: JobTypeInfoItem): void => {
        if (hasPayloadFields(typeInfo) && onRequestRunWithPayload) {
            onRequestRunWithPayload(typeInfo);
        } else if (onRunNow) {
            onRunNow(typeInfo.jobType);
        }
    };

    return (
        <div>
            <div
                className={vertical
                    ? "space-y-3"
                    : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"}
            >
                {jobTypes.map((typeInfo) => (
                    <Card key={typeInfo.jobType}>
                        <CardHeader className="pb-2">
                            <CardTitle className="flex items-center justify-between text-sm">
                                <span>
                                    {typeInfo.label ??
                                        getJobTypeLabel(
                                            typeInfo.jobType,
                                            jobTypeLabels,
                                        )}
                                </span>
                                <StatusDot
                                    status={typeInfo.nextScheduledRun
                                        ? "healthy"
                                        : "unknown"}
                                    pulse={typeInfo.nextScheduledRun !== null}
                                />
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-1">
                            <div className="flex justify-between text-xs text-text-secondary">
                                <span>Last completed</span>
                                <span>
                                    {formatRelativeTime(
                                        typeInfo.lastCompletedAt,
                                    )}
                                </span>
                            </div>
                            <div className="flex justify-between text-xs text-text-secondary">
                                <span>Next scheduled</span>
                                <span>
                                    {formatRelativeTime(
                                        typeInfo.nextScheduledRun,
                                    )}
                                </span>
                            </div>
                            <div className="flex justify-between text-xs text-text-secondary">
                                <span>Total / Failed</span>
                                <span>
                                    {typeInfo.totalJobs}
                                    {typeInfo.failedCount > 0 && (
                                        <span className="text-red-500">
                                            / {typeInfo.failedCount}
                                        </span>
                                    )}
                                </span>
                            </div>
                            {(onRunNow || onRequestRunWithPayload) &&
                                isInvocable(typeInfo) && (
                                <div className="pt-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        className="w-full"
                                        onClick={() => handleRunClick(typeInfo)}
                                    >
                                        <Play className="w-3 h-3 mr-1" />
                                        Run Now
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                ))}
                {jobTypes.length === 0 && (
                    <Card>
                        <CardContent className="py-4 text-center text-text-secondary">
                            No job types found
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};
